import React, { useCallback, useEffect, useState } from 'react';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { Pagination } from '../../components/ui/Pagination';
import { Spinner } from '../../components/ui/Spinner';
import { useToast } from '../../hooks/useToast';
import {
  QualifyingChannel,
  QualifyingTier,
  ReferralPayoutStatus,
  ReferralPayoutsResponse,
  RewardSettings,
  referralsApi,
} from '../../api/referrals';

/**
 * Tier 0 is absent on purpose. A referral reward with no verification
 * requirement pays out to whoever can create the most empty accounts, so the
 * gate can be raised here but never removed — the backend rejects tier_0
 * too, and falls back to Tier 1 if it ever reads one.
 */
const TIER_OPTIONS: Array<{ value: QualifyingTier; label: string; hint: string }> = [
  { value: 'tier_1', label: 'Tier 1', hint: 'BVN verified, with an account' },
  { value: 'tier_2', label: 'Tier 2', hint: 'Plus ID and face verification' },
  { value: 'tier_3', label: 'Tier 3', hint: 'Fully verified, incl. address' },
];

const CHANNEL_OPTIONS: Array<{ value: QualifyingChannel; label: string }> = [
  { value: 'transfer', label: 'Transfers' },
  { value: 'bill', label: 'Bill payments' },
  { value: 'airtime', label: 'Airtime & data' },
];

/**
 * What a referral is worth, and the money that has gone out because of it.
 *
 * The two live together on purpose: the reward amount is the only number on
 * this page that spends real money, so it should be read next to what has
 * already been spent.
 */

const naira = (amount: number) => `₦${Number(amount || 0).toLocaleString('en-NG')}`;

const formatDate = (iso: string | null) =>
  iso
    ? new Date(iso).toLocaleDateString('en-NG', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
    : '—';

const payoutStatusColors: Record<
  ReferralPayoutStatus,
  'warning' | 'success' | 'danger'
> = {
  pending: 'warning',
  paid: 'success',
  failed: 'danger',
};

const inputStyle: React.CSSProperties = {
  height: 38,
  width: 160,
  padding: '0 12px',
  fontSize: 13,
  fontFamily: 'var(--font-family)',
  color: 'var(--neutral-800)',
  background: 'var(--neutral-100)',
  border: '1px solid var(--neutral-300)',
  borderRadius: 'var(--radius-md)',
  outline: 'none',
};

const Field: React.FC<{
  label: string;
  hint: string;
  value: string;
  onChange: (value: string) => void;
  disabled: boolean;
  min?: number;
}> = ({ label, hint, value, onChange, disabled, min = 1 }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
    <label style={{ fontSize: 13, fontWeight: 600, color: 'var(--neutral-800)' }}>
      {label}
    </label>
    <input
      type="number"
      aria-label={label}
      min={min}
      step={1}
      value={value}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
      style={inputStyle}
    />
    <span style={{ fontSize: 12, color: 'var(--neutral-500)', maxWidth: 280 }}>
      {hint}
    </span>
  </div>
);

export const RewardsPanel: React.FC = () => {
  const toast = useToast();

  const [settings, setSettings] = useState<RewardSettings | null>(null);
  const [rewardAmount, setRewardAmount] = useState('');
  const [minimumWithdrawal, setMinimumWithdrawal] = useState('');
  const [qualifyingAmount, setQualifyingAmount] = useState('');
  const [qualifyingTier, setQualifyingTier] = useState<QualifyingTier>('tier_1');
  const [qualifyingChannels, setQualifyingChannels] = useState<QualifyingChannel[]>(
    [],
  );
  const [requiresPayment, setRequiresPayment] = useState(true);
  const [businessReward, setBusinessReward] = useState('');
  const [saving, setSaving] = useState(false);

  const [payouts, setPayouts] = useState<ReferralPayoutsResponse | null>(null);
  const [payoutsLoading, setPayoutsLoading] = useState(true);
  const [page, setPage] = useState(1);

  const applySettings = (next: RewardSettings) => {
    setSettings(next);
    setRewardAmount(String(next.rewardAmount));
    setMinimumWithdrawal(String(next.minimumWithdrawal));
    setQualifyingAmount(String(next.qualifyingAmount));
    setQualifyingTier(next.qualifyingTier);
    setQualifyingChannels(next.qualifyingChannels);
    setRequiresPayment(next.requiresPayment ?? true);
    setBusinessReward(
      next.businessRewardAmount === undefined ? '' : String(next.businessRewardAmount),
    );
  };

  // An older backend has no business reward, and refuses a field it does
  // not know, so the field is neither shown nor sent.
  const hasBusinessReward = settings?.businessRewardAmount !== undefined;

  const fetchPayouts = useCallback(async () => {
    try {
      setPayoutsLoading(true);
      const res = await referralsApi.getPayouts({ page, limit: 10 });
      setPayouts(res);
    } catch (err) {
      toast.error(err, 'Failed to load reward payouts.');
    } finally {
      setPayoutsLoading(false);
    }
  }, [page, toast]);

  useEffect(() => {
    void (async () => {
      try {
        applySettings(await referralsApi.getSettings());
      } catch (err) {
        toast.error(err, 'Failed to load reward settings.');
      }
    })();
    // Settings are fetched once; saving updates them in place.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    void fetchPayouts();
  }, [fetchPayouts]);

  const dirty =
    !!settings &&
    (rewardAmount !== String(settings.rewardAmount) ||
      minimumWithdrawal !== String(settings.minimumWithdrawal) ||
      qualifyingAmount !== String(settings.qualifyingAmount) ||
      qualifyingTier !== settings.qualifyingTier ||
      qualifyingChannels.join(',') !== settings.qualifyingChannels.join(',') ||
      requiresPayment !== (settings.requiresPayment ?? true) ||
      (hasBusinessReward && businessReward !== String(settings.businessRewardAmount)));

  async function handleSave() {
    const reward = Number(rewardAmount);
    const minimum = Number(minimumWithdrawal);
    const qualifying = Number(qualifyingAmount);
    if (!Number.isInteger(reward) || reward <= 0) {
      toast.error('Reward per referral must be a whole number of naira above zero.');
      return;
    }
    if (!Number.isInteger(minimum) || minimum <= 0) {
      toast.error('Minimum withdrawal must be a whole number of naira above zero.');
      return;
    }
    if (!Number.isInteger(qualifying) || qualifying <= 0) {
      toast.error('Qualifying payment must be a whole number of naira above zero.');
      return;
    }
    const business = Number(businessReward);
    if (hasBusinessReward && (businessReward.trim() === '' || !Number.isInteger(business) || business < 0)) {
      toast.error('Reward per business must be a whole number of naira, or 0 to turn it off.');
      return;
    }
    if (!qualifyingChannels.length) {
      toast.error('At least one kind of payment must count towards a reward.');
      return;
    }
    try {
      setSaving(true);
      applySettings(
        await referralsApi.updateSettings({
          rewardAmount: reward,
          minimumWithdrawal: minimum,
          qualifyingAmount: qualifying,
          qualifyingTier,
          qualifyingChannels,
          requiresPayment,
          ...(hasBusinessReward ? { businessRewardAmount: business } : {}),
        }),
      );
      toast.success('Reward settings updated.');
    } catch (err) {
      toast.error(err, 'Could not update reward settings.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <Card>
        <h2 style={{ fontSize: 16, fontWeight: 700, color: 'var(--neutral-800)' }}>
          Reward settings
        </h2>
        <p
          style={{
            margin: '6px 0 0',
            fontSize: 13,
            lineHeight: 1.6,
            color: 'var(--neutral-600)',
            maxWidth: 640,
          }}
        >
          Rewards are earned automatically: once someone you invited verifies
          their identity — <em>and</em>, if the payment rule is on, makes a
          payment that counts, of at least the qualifying amount — the
          referrer earns their reward and can withdraw it. Only the referrer is
          paid. Verification can only be <strong>raised</strong>, never
          switched off, since without it rewards go to whoever can create the
          most empty accounts. The app shows users these rules exactly as set
          here.
        </p>
        <p
          style={{
            margin: '8px 0 0',
            fontSize: 13,
            lineHeight: 1.6,
            color: 'var(--neutral-600)',
            maxWidth: 640,
          }}
        >
          A new reward amount applies to referrals made from now on. Rewards
          already earned keep the amount they were earned at, so a change never
          reprices — or devalues — what someone is already owed.
        </p>

        {settings === null ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 32 }}>
            <Spinner />
          </div>
        ) : (
          <>
            <div
              style={{
                display: 'flex',
                gap: 32,
                marginTop: 20,
                flexWrap: 'wrap',
              }}
            >
              <Field
                label="Reward per referral"
                hint="Earned by the referrer once their invitee qualifies."
                value={rewardAmount}
                onChange={setRewardAmount}
                disabled={saving}
              />
              {hasBusinessReward ? (
                <Field
                  label="Reward per business"
                  hint="Earned once a business that signed up for PayIQ Business with their code is verified and, under the payment rule, makes a qualifying payment. 0 turns it off. Aggregators never get it: they earn a share instead."
                  value={businessReward}
                  onChange={setBusinessReward}
                  disabled={saving}
                  min={0}
                />
              ) : null}
              <Field
                label="Qualifying payment"
                hint="How much an invited user must pay, in one go, for their referrer to earn."
                value={qualifyingAmount}
                onChange={setQualifyingAmount}
                disabled={saving || !requiresPayment}
              />
              <Field
                label="Minimum withdrawal"
                hint="Users must have accrued at least this much before they can withdraw."
                value={minimumWithdrawal}
                onChange={setMinimumWithdrawal}
                disabled={saving}
              />
            </div>

            <div style={{ marginTop: 28, display: 'flex', gap: 32, flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <label
                  style={{ fontSize: 13, fontWeight: 600, color: 'var(--neutral-800)' }}
                >
                  Required verification
                </label>
                <select
                  value={qualifyingTier}
                  disabled={saving}
                  onChange={(e) => setQualifyingTier(e.target.value as QualifyingTier)}
                  style={{ ...inputStyle, width: 220, cursor: 'pointer' }}
                >
                  {TIER_OPTIONS.map((tier) => (
                    <option key={tier.value} value={tier.value}>
                      {tier.label} — {tier.hint}
                    </option>
                  ))}
                </select>
                <span
                  style={{ fontSize: 12, color: 'var(--neutral-500)', maxWidth: 280 }}
                >
                  How far an invited user must verify before they can earn their
                  referrer anything. This can be raised but not removed.
                </span>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <label
                  style={{ fontSize: 13, fontWeight: 600, color: 'var(--neutral-800)' }}
                >
                  Payments that count
                </label>
                <label
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    fontSize: 13,
                    fontWeight: 600,
                    color: 'var(--neutral-800)',
                    cursor: saving ? 'not-allowed' : 'pointer',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={requiresPayment}
                    disabled={saving}
                    onChange={() => setRequiresPayment((on) => !on)}
                  />
                  Require a first payment
                </label>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 6,
                    paddingLeft: 22,
                    opacity: requiresPayment ? 1 : 0.5,
                  }}
                >
                  {CHANNEL_OPTIONS.map((channel) => {
                    const checked = qualifyingChannels.includes(channel.value);
                    // Unchecking the last one is blocked here rather than only
                    // on save: "none" is never a valid state to be sitting in.
                    const isLast = checked && qualifyingChannels.length === 1;
                    return (
                      <label
                        key={channel.value}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          fontSize: 13,
                          color: 'var(--neutral-700)',
                          cursor: isLast || saving ? 'not-allowed' : 'pointer',
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          disabled={saving || isLast || !requiresPayment}
                          onChange={() =>
                            setQualifyingChannels((current) =>
                              checked
                                ? current.filter((c) => c !== channel.value)
                                : [...current, channel.value],
                            )
                          }
                        />
                        {channel.label}
                      </label>
                    );
                  })}
                </div>
                <span
                  style={{ fontSize: 12, color: 'var(--neutral-500)', maxWidth: 280 }}
                >
                  {requiresPayment
                    ? 'At least one must stay on.'
                    : 'Off: verifying alone earns the reward.'}
                </span>
              </div>
            </div>

            <div style={{ marginTop: 24, display: 'flex', alignItems: 'center', gap: 12 }}>
              <Button onClick={() => void handleSave()} loading={saving} disabled={!dirty}>
                Save changes
              </Button>
              {dirty ? (
                <span style={{ fontSize: 12, color: 'var(--neutral-500)' }}>
                  Unsaved changes.
                </span>
              ) : null}
            </div>
          </>
        )}
      </Card>

      <Card padding={20}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 12,
            marginBottom: 16,
            flexWrap: 'wrap',
          }}
        >
          <div>
            <h2 style={{ fontSize: 16, fontWeight: 700, color: 'var(--neutral-800)' }}>
              Reward payouts
            </h2>
            <p style={{ fontSize: 13, color: 'var(--neutral-500)', marginTop: 2 }}>
              Withdrawals from the rewards account into users' wallets.
            </p>
          </div>
          <Badge variant="success">
            {naira(payouts?.summary.totalPaid ?? 0)} paid out
          </Badge>
        </div>

        {payoutsLoading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
            <Spinner />
          </div>
        ) : !payouts?.items.length ? (
          <div
            style={{
              padding: 40,
              textAlign: 'center',
              fontSize: 13,
              color: 'var(--neutral-500)',
            }}
          >
            No one has withdrawn referral earnings yet.
          </div>
        ) : (
          <>
            <div style={{ overflowX: 'auto' }}>
              <table
                style={{
                  width: '100%',
                  minWidth: 760,
                  borderCollapse: 'collapse',
                  fontSize: 13,
                }}
              >
                <thead>
                  <tr style={{ background: 'var(--neutral-200)' }}>
                    {['User', 'Amount', 'Referrals', 'Reference', 'Status', 'Date'].map(
                      (header) => (
                        <th
                          key={header}
                          style={{
                            padding: '12px 14px',
                            textAlign: 'left',
                            fontSize: 12,
                            color: 'var(--neutral-600)',
                            textTransform: 'uppercase',
                            letterSpacing: 0.4,
                          }}
                        >
                          {header}
                        </th>
                      ),
                    )}
                  </tr>
                </thead>
                <tbody>
                  {payouts.items.map((payout) => (
                    <tr key={payout.id}>
                      <td style={{ padding: '12px 14px' }}>
                        <div style={{ fontWeight: 600 }}>
                          {payout.user?.name || 'Unknown user'}
                        </div>
                        <div style={{ color: 'var(--neutral-500)', fontSize: 12 }}>
                          {payout.user?.email ?? '—'}
                        </div>
                      </td>
                      <td style={{ padding: '12px 14px', fontWeight: 600 }}>
                        {naira(payout.amount)}
                      </td>
                      <td style={{ padding: '12px 14px' }}>{payout.referralCount}</td>
                      <td
                        style={{
                          padding: '12px 14px',
                          fontFamily: 'monospace',
                          fontSize: 12,
                          color: 'var(--neutral-600)',
                        }}
                      >
                        {payout.reference}
                      </td>
                      <td style={{ padding: '12px 14px' }}>
                        <Badge variant={payoutStatusColors[payout.status]}>
                          {payout.status}
                        </Badge>
                        {payout.failureReason ? (
                          <div
                            style={{
                              fontSize: 11,
                              color: 'var(--neutral-500)',
                              marginTop: 4,
                              maxWidth: 220,
                            }}
                          >
                            {payout.failureReason}
                          </div>
                        ) : null}
                      </td>
                      <td style={{ padding: '12px 14px', color: 'var(--neutral-600)' }}>
                        {formatDate(payout.paidAt ?? payout.createdAt)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <Pagination
              page={payouts.meta.page}
              totalPages={payouts.meta.totalPages}
              total={payouts.meta.total}
              onPageChange={setPage}
            />
          </>
        )}
      </Card>
    </div>
  );
};
